import { type Static, Type } from "@sinclair/typebox";
import { Value } from "@sinclair/typebox/value";
import {
  BusinessOperationIdSchema,
  type BusinessOperationTraceV1,
  BusinessOperationTraceV1Schema,
  isBusinessOperationTraceV1,
} from "./business-operation.js";
import {
  derivePurchaseOrderNumber,
  type PurchaseOrderNumber,
  PurchaseOrderNumberSchema,
} from "./purchase-order-number.js";

const StableIdSchema = Type.String({
  minLength: 1,
  maxLength: 64,
  pattern: "^[A-Za-z0-9][A-Za-z0-9._-]*$",
});
const CanonicalTextPattern = "^(?:\\S|\\S[\\s\\S]*\\S)$";
const CanonicalTextSchema = (maximum: number) =>
  Type.String({ minLength: 1, maxLength: maximum, pattern: CanonicalTextPattern });
const NullableCanonicalTextSchema = (maximum: number) =>
  Type.Union([CanonicalTextSchema(maximum), Type.Null()]);
const BusinessDateSchema = Type.String({ pattern: "^[0-9]{4}-[0-9]{2}-[0-9]{2}$" });
const VersionSchema = Type.Integer({ minimum: 1, maximum: Number.MAX_SAFE_INTEGER });
const ExpectedVersionSchema = Type.Integer({ minimum: 0, maximum: Number.MAX_SAFE_INTEGER - 1 });
const QuantitySchema = Type.Integer({ minimum: 1, maximum: 1_000_000 });
const BaseQuantitySchema = Type.Integer({ minimum: 1, maximum: 1_000_000_000_000 });
const UnitPriceFenSchema = Type.Integer({ minimum: 0, maximum: 100_000_000 });
const TaxFenSchema = Type.Integer({ minimum: 0, maximum: 1_000_000_000_000 });
const AmountFenSchema = Type.Integer({ minimum: 0, maximum: Number.MAX_SAFE_INTEGER });
const IdempotencyKeySchema = Type.String({ minLength: 8, maxLength: 128 });
const EvidenceSchema = Type.Union([Type.Literal("SIMULATED"), Type.Literal("STORE_ACCEPTED")]);

export const PurchaseOrderStatusSchema = Type.Union([
  Type.Literal("DRAFT"),
  Type.Literal("PENDING_APPROVAL"),
  Type.Literal("APPROVED"),
  Type.Literal("REJECTED"),
  Type.Literal("PARTIALLY_RECEIVED"),
  Type.Literal("RECEIVED"),
  Type.Literal("CANCELLED"),
  Type.Literal("CLOSED"),
]);
export type PurchaseOrderStatus = Static<typeof PurchaseOrderStatusSchema>;

const PURCHASE_ORDER_TRANSITIONS: Readonly<
  Record<PurchaseOrderStatus, readonly PurchaseOrderStatus[]>
> = {
  DRAFT: ["PENDING_APPROVAL", "CANCELLED"],
  PENDING_APPROVAL: ["APPROVED", "REJECTED", "CANCELLED"],
  APPROVED: ["PARTIALLY_RECEIVED", "RECEIVED", "CANCELLED"],
  REJECTED: ["DRAFT", "CANCELLED"],
  PARTIALLY_RECEIVED: ["PARTIALLY_RECEIVED", "RECEIVED", "CLOSED"],
  RECEIVED: ["CLOSED"],
  CANCELLED: [],
  CLOSED: [],
};

export function canTransitionPurchaseOrderStatus(
  from: PurchaseOrderStatus,
  to: PurchaseOrderStatus,
): boolean {
  return PURCHASE_ORDER_TRANSITIONS[from].includes(to);
}

export const PurchaseOrderLineUnitSelectionSchema = Type.Union([
  Type.Object(
    {
      kind: Type.Literal("BASE"),
      conversionId: Type.Null(),
      expectedConversionVersion: Type.Null(),
    },
    { additionalProperties: false },
  ),
  Type.Object(
    {
      kind: Type.Literal("CONVERSION"),
      conversionId: StableIdSchema,
      expectedConversionVersion: VersionSchema,
    },
    { additionalProperties: false },
  ),
]);
export type PurchaseOrderLineUnitSelection = Static<typeof PurchaseOrderLineUnitSelectionSchema>;

export const PurchaseOrderDraftLineRequestSchema = Type.Object(
  {
    lineId: StableIdSchema,
    skuId: StableIdSchema,
    expectedProductVersion: VersionSchema,
    unitSelection: PurchaseOrderLineUnitSelectionSchema,
    inputUnitQuantity: QuantitySchema,
    inputUnitPriceFen: UnitPriceFenSchema,
    taxFen: TaxFenSchema,
  },
  { additionalProperties: false },
);
export type PurchaseOrderDraftLineRequest = Static<typeof PurchaseOrderDraftLineRequestSchema>;

export const PurchaseOrderDraftUpsertRequestSchema = Type.Object(
  {
    purchaseOrderId: StableIdSchema,
    businessDate: BusinessDateSchema,
    supplierId: StableIdSchema,
    expectedSupplierVersion: VersionSchema,
    expectedDeliveryDate: Type.Union([BusinessDateSchema, Type.Null()]),
    notes: NullableCanonicalTextSchema(500),
    lines: Type.Array(PurchaseOrderDraftLineRequestSchema, { minItems: 1, maxItems: 200 }),
    expectedVersion: ExpectedVersionSchema,
    operationTrace: BusinessOperationTraceV1Schema,
    idempotencyKey: IdempotencyKeySchema,
  },
  { additionalProperties: false },
);
export type PurchaseOrderDraftUpsertRequest = Static<typeof PurchaseOrderDraftUpsertRequestSchema>;

export const PurchaseOrderSupplierSnapshotSchema = Type.Object(
  {
    supplierId: StableIdSchema,
    supplierCode: Type.String({
      minLength: 1,
      maxLength: 40,
      pattern: "^[A-Za-z0-9][A-Za-z0-9._-]*$",
    }),
    name: CanonicalTextSchema(80),
    version: VersionSchema,
    paymentTermsDays: Type.Integer({ minimum: 0, maximum: 3_650 }),
  },
  { additionalProperties: false },
);
export type PurchaseOrderSupplierSnapshot = Static<typeof PurchaseOrderSupplierSnapshotSchema>;

export const PurchaseOrderDraftLineRecordSchema = Type.Object(
  {
    lineId: StableIdSchema,
    skuId: StableIdSchema,
    productName: CanonicalTextSchema(80),
    productVersion: VersionSchema,
    inputUnitName: CanonicalTextSchema(16),
    inputUnitQuantity: QuantitySchema,
    baseUnitName: CanonicalTextSchema(16),
    baseUnitQuantityPerInput: Type.Integer({ minimum: 1, maximum: 1_000_000 }),
    orderedBaseQuantity: BaseQuantitySchema,
    inputUnitPriceFen: UnitPriceFenSchema,
    subtotalFen: AmountFenSchema,
    taxFen: TaxFenSchema,
    totalFen: AmountFenSchema,
    conversionId: Type.Union([StableIdSchema, Type.Null()]),
    conversionVersion: Type.Union([VersionSchema, Type.Null()]),
  },
  { additionalProperties: false },
);
export type PurchaseOrderDraftLineRecord = Static<typeof PurchaseOrderDraftLineRecordSchema>;

export const PurchaseOrderDraftRecordSchema = Type.Object(
  {
    purchaseOrderId: StableIdSchema,
    purchaseOrderNumber: PurchaseOrderNumberSchema,
    businessDate: BusinessDateSchema,
    status: Type.Literal("DRAFT"),
    supplier: PurchaseOrderSupplierSnapshotSchema,
    expectedDeliveryDate: Type.Union([BusinessDateSchema, Type.Null()]),
    notes: NullableCanonicalTextSchema(500),
    lines: Type.Array(PurchaseOrderDraftLineRecordSchema, { minItems: 1, maxItems: 200 }),
    subtotalFen: AmountFenSchema,
    taxFen: AmountFenSchema,
    totalFen: AmountFenSchema,
    inventoryEffect: Type.Literal("NONE"),
    payableEffect: Type.Literal("NONE"),
    version: VersionSchema,
    lastCommandId: BusinessOperationIdSchema,
    operationTrace: BusinessOperationTraceV1Schema,
    createdAt: Type.String({ minLength: 1, maxLength: 64 }),
    updatedAt: Type.String({ minLength: 1, maxLength: 64 }),
    evidence: EvidenceSchema,
  },
  { additionalProperties: false },
);
export type PurchaseOrderDraftRecord = Static<typeof PurchaseOrderDraftRecordSchema>;

export const PurchaseOrderDraftRecordListSchema = Type.Array(PurchaseOrderDraftRecordSchema, {
  maxItems: 500,
});

function isCanonicalBusinessDate(value: string): boolean {
  const milliseconds = Date.parse(`${value}T00:00:00.000Z`);
  return (
    Number.isFinite(milliseconds) &&
    new Date(milliseconds).toISOString() === `${value}T00:00:00.000Z`
  );
}

function isCanonicalTimestamp(value: string): boolean {
  const milliseconds = Date.parse(value);
  return Number.isFinite(milliseconds) && new Date(milliseconds).toISOString() === value;
}

function purchaseOrderCorrelationId(purchaseOrderId: string): string {
  return `purchase-order:${purchaseOrderId}`;
}

function traceMatchesPurchaseOrder(
  trace: BusinessOperationTraceV1,
  input: {
    purchaseOrderId: string;
    purchaseOrderNumber: PurchaseOrderNumber;
    idempotencyKey: string;
  },
): boolean {
  return (
    isBusinessOperationTraceV1(trace) &&
    trace.commandId === input.idempotencyKey &&
    trace.correlationId === purchaseOrderCorrelationId(input.purchaseOrderId) &&
    trace.idempotencyKey === input.idempotencyKey &&
    trace.businessNumber?.kind === "PURCHASE_ORDER" &&
    trace.businessNumber.value === input.purchaseOrderNumber
  );
}

function safeAdd(values: readonly number[]): number | null {
  let total = 0;
  for (const value of values) {
    if (!Number.isSafeInteger(value)) return null;
    total += value;
    if (!Number.isSafeInteger(total)) return null;
  }
  return total;
}

export function isPurchaseOrderDraftUpsertRequest(
  value: unknown,
): value is PurchaseOrderDraftUpsertRequest {
  if (!Value.Check(PurchaseOrderDraftUpsertRequestSchema, value)) return false;
  const request = value as PurchaseOrderDraftUpsertRequest;
  if (
    !isCanonicalBusinessDate(request.businessDate) ||
    (request.expectedDeliveryDate !== null &&
      (!isCanonicalBusinessDate(request.expectedDeliveryDate) ||
        request.expectedDeliveryDate < request.businessDate)) ||
    (request.notes !== null && request.notes !== request.notes.trim())
  ) {
    return false;
  }
  let purchaseOrderNumber: PurchaseOrderNumber;
  try {
    purchaseOrderNumber = derivePurchaseOrderNumber(request.businessDate, request.purchaseOrderId);
  } catch {
    return false;
  }
  if (
    !traceMatchesPurchaseOrder(request.operationTrace, {
      purchaseOrderId: request.purchaseOrderId,
      purchaseOrderNumber,
      idempotencyKey: request.idempotencyKey,
    }) ||
    (request.expectedVersion === 0
      ? request.operationTrace.causationId !== null
      : request.operationTrace.causationId === null)
  ) {
    return false;
  }
  const lineIds = new Set<string>();
  const subtotals: number[] = [];
  const taxes: number[] = [];
  for (const line of request.lines) {
    if (lineIds.has(line.lineId)) return false;
    lineIds.add(line.lineId);
    const subtotalFen = line.inputUnitQuantity * line.inputUnitPriceFen;
    if (!Number.isSafeInteger(subtotalFen) || !Number.isSafeInteger(subtotalFen + line.taxFen)) {
      return false;
    }
    subtotals.push(subtotalFen);
    taxes.push(line.taxFen);
  }
  const subtotalFen = safeAdd(subtotals);
  const taxFen = safeAdd(taxes);
  return subtotalFen !== null && taxFen !== null && Number.isSafeInteger(subtotalFen + taxFen);
}

/**
 * Mandatory state-aware write gate. The request-only guard above proves shape
 * and internal arithmetic; it cannot prove which immutable version is the
 * direct parent. Every persistence writer must also call this helper while the
 * current record is locked.
 */
export function isPurchaseOrderDraftUpsertAgainstCurrent(
  request: unknown,
  current: PurchaseOrderDraftRecord | null,
): request is PurchaseOrderDraftUpsertRequest {
  if (!isPurchaseOrderDraftUpsertRequest(request)) return false;
  if (current === null) return request.expectedVersion === 0;
  if (
    !isPurchaseOrderDraftRecord(current) ||
    request.purchaseOrderId !== current.purchaseOrderId ||
    request.expectedVersion !== current.version ||
    request.businessDate !== current.businessDate ||
    request.operationTrace.commandId === current.lastCommandId ||
    request.operationTrace.causationId !== current.lastCommandId ||
    request.operationTrace.correlationId !== current.operationTrace.correlationId ||
    request.operationTrace.businessNumber?.kind !== "PURCHASE_ORDER" ||
    current.operationTrace.businessNumber?.kind !== "PURCHASE_ORDER" ||
    request.operationTrace.businessNumber.value !== current.purchaseOrderNumber ||
    request.operationTrace.businessNumber.value !== current.operationTrace.businessNumber.value
  ) {
    return false;
  }
  return true;
}

export function isPurchaseOrderDraftRecord(value: unknown): value is PurchaseOrderDraftRecord {
  if (!Value.Check(PurchaseOrderDraftRecordSchema, value)) return false;
  const record = value as PurchaseOrderDraftRecord;
  let derivedNumber: PurchaseOrderNumber;
  try {
    derivedNumber = derivePurchaseOrderNumber(record.businessDate, record.purchaseOrderId);
  } catch {
    return false;
  }
  if (
    !isCanonicalBusinessDate(record.businessDate) ||
    (record.expectedDeliveryDate !== null &&
      (!isCanonicalBusinessDate(record.expectedDeliveryDate) ||
        record.expectedDeliveryDate < record.businessDate)) ||
    (record.notes !== null && record.notes !== record.notes.trim()) ||
    !isCanonicalTimestamp(record.createdAt) ||
    !isCanonicalTimestamp(record.updatedAt) ||
    record.updatedAt < record.createdAt ||
    record.purchaseOrderNumber !== derivedNumber ||
    record.lastCommandId !== record.operationTrace.commandId ||
    (record.version === 1
      ? record.operationTrace.causationId !== null
      : record.operationTrace.causationId === null) ||
    !traceMatchesPurchaseOrder(record.operationTrace, {
      purchaseOrderId: record.purchaseOrderId,
      purchaseOrderNumber: record.purchaseOrderNumber,
      idempotencyKey: record.operationTrace.idempotencyKey,
    }) ||
    record.supplier.name !== record.supplier.name.trim()
  ) {
    return false;
  }
  const lineIds = new Set<string>();
  const subtotals: number[] = [];
  const taxes: number[] = [];
  const totals: number[] = [];
  for (const line of record.lines) {
    if (
      lineIds.has(line.lineId) ||
      line.productName !== line.productName.trim() ||
      line.inputUnitName !== line.inputUnitName.trim() ||
      line.baseUnitName !== line.baseUnitName.trim() ||
      (line.conversionId === null) !== (line.conversionVersion === null)
    ) {
      return false;
    }
    lineIds.add(line.lineId);
    const orderedBaseQuantity = line.inputUnitQuantity * line.baseUnitQuantityPerInput;
    const subtotalFen = line.inputUnitQuantity * line.inputUnitPriceFen;
    const totalFen = subtotalFen + line.taxFen;
    if (
      !Number.isSafeInteger(orderedBaseQuantity) ||
      !Number.isSafeInteger(subtotalFen) ||
      !Number.isSafeInteger(totalFen) ||
      line.orderedBaseQuantity !== orderedBaseQuantity ||
      line.subtotalFen !== subtotalFen ||
      line.totalFen !== totalFen ||
      (line.conversionId === null &&
        (line.baseUnitQuantityPerInput !== 1 || line.inputUnitName !== line.baseUnitName)) ||
      (line.conversionId !== null &&
        (line.baseUnitQuantityPerInput < 2 ||
          line.inputUnitName.toLocaleLowerCase("zh-CN") ===
            line.baseUnitName.toLocaleLowerCase("zh-CN")))
    ) {
      return false;
    }
    subtotals.push(line.subtotalFen);
    taxes.push(line.taxFen);
    totals.push(line.totalFen);
  }
  const subtotalFen = safeAdd(subtotals);
  const taxFen = safeAdd(taxes);
  const totalFen = safeAdd(totals);
  return (
    subtotalFen !== null &&
    taxFen !== null &&
    totalFen !== null &&
    record.subtotalFen === subtotalFen &&
    record.taxFen === taxFen &&
    record.totalFen === totalFen &&
    record.totalFen === record.subtotalFen + record.taxFen
  );
}

export function isPurchaseOrderDraftRecordList(
  value: unknown,
): value is PurchaseOrderDraftRecord[] {
  if (!Value.Check(PurchaseOrderDraftRecordListSchema, value)) return false;
  const ids = new Set<string>();
  const numbers = new Set<string>();
  for (const record of value) {
    if (
      !isPurchaseOrderDraftRecord(record) ||
      ids.has(record.purchaseOrderId) ||
      numbers.has(record.purchaseOrderNumber)
    ) {
      return false;
    }
    ids.add(record.purchaseOrderId);
    numbers.add(record.purchaseOrderNumber);
  }
  return true;
}
